import { filterHasValue } from '@akin-home/arrays';
import { getDateDifference, sameTimeOfDay } from '@akin-home/dates';
import { Record } from '@akin-home/records';
import { addYears } from 'date-fns';
import { RRule } from 'rrule';
import { NON_WEEKEND_RECURRENCE_WEEKDAY_IDS } from '../data';
import { Recurrable, Recurrence, RecurrenceMode } from '../types';

export * from './setRecurrenceDays';

export const getRecurrenceDisplayText = (
  dtstart: Date,
  recurrence?: Recurrence
): string => {
  if (!recurrence) return 'Does not repeat';

  let ruleText = 'Custom';

  try {
    ruleText = new RRule({
      dtstart,
      freq: recurrence.freq || undefined,
      interval: recurrence.interval || 1,
      byweekday: recurrence.byweekday || undefined,
      until: getUntilForDisplay(recurrence),
      count: recurrence.count,
    }).toText();
  } catch (e) {
    return `${e}`;
    // The toText failed. Just show custom.
  }

  return ruleText;
};

/** Gets the until date for the recurrence for display. */
const getUntilForDisplay = (recurrence: Recurrence) =>
  shouldShowRecurrenceUntil(recurrence) ? recurrence.until : undefined;

const shouldShowRecurrenceUntil = (recurrence: Recurrence) =>
  recurrence.until && recurrence.until < new Date(9000, 1, 1);

export const createRRule = (recurrence: Recurrence) => ({
  mode: recurrence.mode as string,
  freq: recurrence.freq as number,
  interval: recurrence.interval as number | undefined,
  byweekday: recurrence.byweekday as number[] | undefined,
  until: recurrence.until ? new Date(recurrence.until) : undefined,
  count: recurrence.count ? recurrence.count : undefined,
});

export const serializeRRule = (recurrence: Recurrence) => ({
  ...recurrence,
  until:
    recurrence.until instanceof Date
      ? recurrence.until?.toISOString()
      : recurrence.until,
});

export const createRecurrenceID = (
  originID: string,
  recurrenceDate: Date
): string => originID + '#' + recurrenceDate.toISOString();

/**
 * Computes the until date for generating recurrences.
 * Either use the given until date or 20 years into the future.
 *
 * This is deemed to be far enough into the future whilst not affecting performance too much.
 */
export const computeUntilDate = (recurrence: Recurrence): Date => {
  if (!recurrence.until) {
    return addYears(new Date(), 20);
  }

  if (typeof recurrence.until === 'string') return new Date(recurrence.until);

  return recurrence.until;
};

export const getUntilDateString = (recurrence?: Recurrence) => {
  if (!recurrence) return;

  return computeUntilDate(recurrence).toISOString();
};

export const clearRecurrenceAttributes = <T>(
  item: Recurrable<T>
): Recurrable<T> => ({
  ...item,
  recurrence: undefined,
  recurrenceUntil: undefined,
});

/** Makes the recurrence "Daily" */
export const makeDailyRecurrence = (
  recurrence?: Partial<Recurrence>
): Recurrence => ({
  ...recurrence,
  mode: RecurrenceMode.DAILY,
  freq: RRule.DAILY,
  interval: 1,
  byweekday: recurrence?.byweekday || NON_WEEKEND_RECURRENCE_WEEKDAY_IDS,
});

export const makeWeeklyRecurrence = (
  recurrence?: Partial<Recurrence>
): Recurrence => ({
  ...recurrence,
  mode: RecurrenceMode.WEEKLY,
  freq: RRule.WEEKLY,
  interval: 1,
  byweekday: undefined,
});

export const makeMonthlyRecurrence = (
  recurrence?: Partial<Recurrence>
): Recurrence => ({
  ...recurrence,
  mode: RecurrenceMode.MONTHLY,
  freq: RRule.MONTHLY,
  interval: 1,
  byweekday: undefined,
});

/** Makes the recurrence "Every Weekday" */
export const makeWeekdayRecurrence = (
  recurrence: Partial<Recurrence> & { byweekday: number[] }
): Recurrence => ({
  ...recurrence,
  mode: RecurrenceMode.WEEKDAYS,
  freq: RRule.DAILY,
  byweekday: recurrence.byweekday,
});

export const getRecurrenceMode = <T>(item: Recurrable<T>) => {
  if (item.isRecurrence && item.origin) {
    return item.origin.recurrence?.mode;
  }

  return item.recurrence?.mode;
};

export const getRecurrenceModePluralText = (mode?: RecurrenceMode) => {
  if (!mode) return '';

  return {
    [RecurrenceMode.DAILY]: 'days',
    [RecurrenceMode.WEEKLY]: 'weeks',
    [RecurrenceMode.MONTHLY]: 'months',
    [RecurrenceMode.CUSTOM]: '',
    [RecurrenceMode.WEEKDAYS]: 'weekdays',
  }[mode];
};

export const getRecurrenceModeText = (recurrence?: Recurrence): string => {
  if (!recurrence) return 'Does not repeat';

  switch (recurrence.mode) {
    case RecurrenceMode.DAILY:
      return 'Daily';
    case RecurrenceMode.WEEKLY:
      return 'Weekly';
    case RecurrenceMode.MONTHLY:
      return 'Monthly';
    case RecurrenceMode.CUSTOM:
      return 'Custom';
    default:
      return 'Custom';
  }
};

export const flattenWithRecurrences = <T>(
  baseItems: Recurrable<T>[]
): (T | Recurrable<T>)[] => [
  ...baseItems,
  ...baseItems.map((i) => i.recurrences || []).flat(),
];

type RecurrenceGeneratorFunction<T> = (params: {
  item: T;
  recurrenceDate: Date;
}) => T;

/** Produces the recurrences for the given item */
export const generateRecurrences =
  <T extends Recurrable>(generator: RecurrenceGeneratorFunction<T>) =>
  (latestDate?: Date, earliestDate?: Date) =>
  (item: T): T => ({
    ...item,
    recurrences: generateRecurrenceDates({
      item,
      latestDate,
      earliestDate,
    }).map((recurrenceDate) => generator({ item, recurrenceDate })),
  });

// Generates recurrences dates for a given calendar item and date range
export const generateRecurrenceDates = <T>({
  item,
  latestDate,
  earliestDate,
}: {
  item: Recurrable<T>;
  latestDate?: Date;
  earliestDate?: Date;
}): Date[] => {
  const rrule = item.recurrence;

  if (!rrule) return [];

  // Calculate the "until" limit
  const until = getLatestRecurrenceDate(rrule.until, latestDate);
  const { start } = item;

  const dateGenerator = createRecurrenceRule({
    rrule,
    start,
    until,
  });

  const dateStrings = earliestDate
    ? dateGenerator.between(earliestDate, until)
    : dateGenerator.all();

  const dates = dateStrings
    .map((d) => {
      if (sameTimeOfDay(d, start)) {
        return d;
      } else {
        /** This is to resolve issues arising from daylight savings time */
        d.setHours(start.getHours());
        return d;
      }
    })
    .filter((d) => d > start);

  if (Object.keys(item.recurrenceExcludeDates).length) {
    // Filter out the dates that are included in the excluded dates map
    return dates.filter(filterExcludedDates(item.recurrenceExcludeDates));
  } else {
    return dates;
  }
};

// Creates recurrence rule instance for use with the rrule library
export const createRecurrenceRule = ({
  rrule,
  start,
  until,
  tzid,
}: {
  rrule: Recurrence;
  start: Date;
  until: Date;
  tzid?: string;
}): RRule => {
  return new RRule({
    freq: rrule.freq,
    dtstart: start,
    interval: rrule.interval || 1,
    byweekday: rrule.byweekday,
    tzid,
    until,
  });
};

// Gets the latest date for limiting the generation of recurrences
export const getLatestRecurrenceDate = (
  until?: Date,
  latestFilterDate?: Date | null
): Date => {
  if (
    (!until && latestFilterDate) ||
    (until && latestFilterDate && latestFilterDate < until)
  ) {
    until = latestFilterDate;
  } else if (!until) {
    until = new Date(2030, 1, 1);
  }

  return until;
};

export const filterExcludedDates =
  (exclusionMap: { [key: string]: true }) => (date: Date) =>
    !exclusionMap[date.toISOString()];

// Base function for creating a recurring item
export const createRecurringItem = <T extends Record & { start: Date }>({
  item,
  recurrenceDate,
}: {
  item: T;
  recurrenceDate: Date;
}): T => ({
  ...item,
  id: createRecurrenceID(item.id, recurrenceDate),
  start: recurrenceDate,
  isRecurrence: true,
  recurrence: undefined,
  recurrences: undefined,
  recurrenceUntil: undefined,
  origin: item,
});

export const getDateFromRecurrenceID = (recurrenceID: string): Date =>
  new Date(getDateStringFromRecurrenceID(recurrenceID));

export const getDateStringFromRecurrenceID = (recurrenceID: string): string =>
  recurrenceID.split('#')[1];

/** Gets the origin ID from a recurrence ID. Returns undefined if the given ID was not a recurrence ID. */
export const getOriginIDFromRecurrenceID = (id: string) => {
  if (!id.includes('#')) return;

  return id.split('#')[0];
};

// Gets the custom reccurence data for a recurring item
export const getCustomRecurrenceData = <T>(item: Recurrable<T>): any =>
  item.recurrenceInstanceData || {};

// Adds the end date to a recurring item
export const addRecurrenceEndDate =
  <T extends { start: Date; end: Date }>(origin: T) =>
  (recurringItem: T): T => ({
    ...recurringItem,
    // Add recurrence end date by adding the difference of the original dates to the recurring start date
    end: new Date(
      recurringItem.start.getTime() +
        getDateDifference(origin.end, origin.start)
    ),
  });

export const setRecurrenceInterval = (
  recurrence?: Recurrence,
  interval?: number
) => {
  if (!recurrence) return;

  return {
    ...recurrence,
    interval,
  };
};

export const getFrequencyFromMode = (mode: RecurrenceMode) => {
  switch (mode) {
    case RecurrenceMode.DAILY:
      return RRule.DAILY;
    case RecurrenceMode.WEEKLY:
      return RRule.WEEKLY;
    case RecurrenceMode.MONTHLY:
      return RRule.MONTHLY;
    default:
      return RRule.DAILY;
  }
};

export const processWeekdays = (weekday_string: string) => {
  const days = weekday_string.split(',');

  return filterHasValue(days.map((day) => day_number_map[day.toLowerCase()]));
};

const day_number_map: { [key: string]: number } = {
  monday: 0,
  mon: 0,
  tuesday: 1,
  tue: 1,
  wednesday: 2,
  wed: 2,
  thursday: 3,
  thu: 3,
  friday: 4,
  fri: 4,
  saturday: 5,
  sat: 5,
  sunday: 6,
  sun: 6,
};
